import { useState, useEffect, useMemo, useCallback } from 'react';
import { computeValidCodes } from '../utils/approvalStateUtils';

/**
 * Approval-tracking hook for Autism review document pages (RD_*_Autism.js).
 *
 * Mirrors useApprovalTracking but reads from a reportContent subcollection
 * document (passed in directly) instead of the monolithic ADHD patient doc.
 * The Autism data model stores outputs, inputs, and approvedParagraphs as
 * top-level fields on each reportContent/{stageKey} document, so this hook
 * accepts them directly rather than deriving them from a nested path.
 *
 * Computes totalParagraphs from the current docStructure + outputs, loads
 * approvedParagraphs from the passed-in reportData, and intersects them
 * with the set of currently valid paragraph codes. Orphaned approval keys
 * (from paragraphs that no longer exist or were blanked) are excluded,
 * preventing them from inflating reviewedCodes and blocking isFullyApproved.
 *
 * @param {Object}  params
 * @param {Object}  params.docStructure        - Section -> paragraph map with { code } entries
 * @param {Object}  params.reportDataOutputs   - Outputs from reportContent doc (code -> content)
 * @param {Object}  params.reportDataApproved  - approvedParagraphs from reportContent doc
 * @param {Set<string>} [params.excludedCodes] - Optional codes to exclude from validity
 *
 * @returns {{
 *   totalParagraphs: number,
 *   reviewedCodes: Set<string>,
 *   isFullyApproved: boolean,
 *   showWarning: boolean,
 *   addReviewedCode: (code: string) => void,
 *   removeReviewedCode: (code: string) => void,
 * }}
 */
const EMPTY_SET = new Set();

export function useModularApprovalTracking({
  docStructure,
  reportDataOutputs,
  reportDataApproved,
  excludedCodes = EMPTY_SET,
}) {
  const [reviewedCodes, setReviewedCodes] = useState(new Set());
  const [totalParagraphs, setTotalParagraphs] = useState(0);

  // Derive the set of paragraph codes that currently have content and are
  // not explicitly excluded. Only these codes count towards approval tracking;
  // any approvedParagraphs keys outside this set are stale leftovers.
  //
  // Uses the same shared computeValidCodes helper as the ADHD hook and the
  // exit-transaction write, so validity logic stays identical everywhere.
  const validCodes = useMemo(
    () => computeValidCodes(docStructure, reportDataOutputs, excludedCodes),
    [docStructure, reportDataOutputs, excludedCodes]
  );

  useEffect(() => {
    setTotalParagraphs(validCodes.size);
  }, [validCodes]);

  // Seed reviewedCodes from the reportContent doc's approvedParagraphs,
  // intersected with validCodes to filter out orphaned keys.
  useEffect(() => {
    if (!reportDataApproved || validCodes.size === 0) return;

    const filtered = new Set(
      Object.keys(reportDataApproved).filter(code => validCodes.has(code))
    );
    setReviewedCodes(filtered);
  }, [reportDataApproved, validCodes]);

  const isFullyApproved =
    totalParagraphs > 0 && totalParagraphs === reviewedCodes.size;

  const showWarning = totalParagraphs > reviewedCodes.size;

  const addReviewedCode = useCallback(code => {
    setReviewedCodes(prev => new Set([...prev, code]));
  }, []);

  const removeReviewedCode = useCallback(code => {
    setReviewedCodes(prev => {
      const next = new Set(prev);
      next.delete(code);
      return next;
    });
  }, []);

  return {
    totalParagraphs,
    reviewedCodes,
    isFullyApproved,
    showWarning,
    addReviewedCode,
    removeReviewedCode,
  };
}
